"use client"

import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useShoppingCart } from '@/src/context/shopping-cart/shopping-cart-context'
import ActionButton from '@/src/components/misc/action-elements/action-button/action-button'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import { useCustomer } from '@/src/context/customer/customer-context'
import NextOpeningHour from '../../misc/next-opening-hour/next-opening-hour'
import { useGoogleAnalytics } from '@/src/context/google-analytics/google-analytics-context'

interface Props {
    showContinueButton?: boolean
}

export default function PriceSummary({ showContinueButton = true }: Props) {
    const t = useTranslations('shopping-cart')
    const { merchantDetailed, polling } = useMerchant()
    const { customerLoginStatus } = useCustomer()
    const { beginCheckoutGA } = useGoogleAnalytics()

    const {
        shoppingCart,
        totalItemsPrice,
        totalPriceWithDiscount,
        totalDiscountPrice,
        cashbackCalculatedPrice,
        cashbackFullPrice
    } = useShoppingCart()

    const [continueCheckout, setContinueCheckout] = useState(false)
    const [canGoToCheckout, setCanGoToCheckout] = useState(true)

    const allowsSchedule = merchantDetailed?.model?.scheduleConfiguration?.schedulingOrdersAllowed
    const openingHours = merchantDetailed?.model?.openingHours

    useEffect(() => {
        if (merchantDetailed?.model?.customerAuthScheme === 'Default' && customerLoginStatus !== 'loggedIn') {
            setCanGoToCheckout(false)
        }
    }, [merchantDetailed, customerLoginStatus])

    useEffect(() => {
        if (continueCheckout) {
            beginCheckoutGA({
                currency: 'BRL',
                value: totalPriceWithDiscount,
                coupon: shoppingCart?.coupon?.name,
                items: (shoppingCart?.items || []).map(item => ({
                    item_id: item.id,
                    item_name: item.name,
                }))
            })
        }
    }, [continueCheckout])

    return (
        <div className={styles.container}>
            <div className={styles.prices}>
                {
                    (totalDiscountPrice > 0 || cashbackFullPrice > 0) &&
                    <>
                        <div className={styles.resumeArea}>
                            <span>{t('itemsTotalPrice')}:</span>
                            <span>{totalItemsPrice.toLocalePrice()}</span>
                        </div>
                        {
                            (shoppingCart?.fee && shoppingCart.fee > 0)
                                ?
                                <div className={styles.resumeArea}>
                                    <span>Taxa de entrega:</span>
                                    <span>{shoppingCart.fee.toLocalePrice()}</span>
                                </div>
                                :
                                <></>
                        }
                        {
                            cashbackFullPrice !== cashbackCalculatedPrice
                            &&
                            <div className={styles.discountArea}>
                                <small>Total cashback:</small>
                                <small>-{cashbackFullPrice.toLocalePrice()} </small>
                            </div>
                        }
                        {
                            shoppingCart?.coupon?.applyType !== 'Valor da Entrega' &&
                            <div className={styles.discountArea}>
                                {
                                    (cashbackFullPrice !== cashbackCalculatedPrice)
                                        ? <span>{t('discount')}:</span>
                                        : cashbackFullPrice > 0
                                            ? <span>{t('discount')} (cashback)</span>
                                            : <span>{t('discount')} (cupom)</span>
                                }
                                {
                                    cashbackFullPrice > 0
                                        ?
                                        <span>-{cashbackCalculatedPrice.toLocalePrice()} </span>
                                        :
                                        <span>-{totalDiscountPrice.toLocalePrice()} </span>
                                }
                            </div>
                        }
                    </>
                }
                <div className={styles.bottomResumeArea}>
                    <span className='font-lg'>{showContinueButton ? 'Subtotal' : 'Total'}:</span>
                    {
                        totalPriceWithDiscount < 0
                            ?
                            <span className='font-lg'>{(0).toLocalePrice()}</span>
                            :
                            <span className='font-lg'>{totalPriceWithDiscount.toLocalePrice()}</span>
                    }
                </div>
            </div>
            {
                (!polling?.model?.isOpened && !allowsSchedule && openingHours)
                    ?
                    <NextOpeningHour openingHours={openingHours} />
                    :
                    showContinueButton &&
                    <Link className={styles.link} href={canGoToCheckout ? '/carrinho' : '/entrar?redirect=carrinho'}>
                        <ActionButton size='medium' onClick={() => setContinueCheckout(true)}>{t('continueCheckout')}</ActionButton>
                    </Link>
            }
        </div>
    )
}
